import { ContextFormFieldDate } from '@bambi/component-library/src/components/forms/context/ContextFormFieldDate';
import { ContextFormFieldEmail } from '@bambi/component-library/src/components/forms/context/ContextFormFieldEmail';
import { ContextFormFieldNumber } from '@bambi/component-library/src/components/forms/context/ContextFormFieldNumber';
import { ContextFormFieldPhone } from '@bambi/component-library/src/components/forms/context/ContextFormFieldPhone';
import { ContextFormFieldSelect } from '@bambi/component-library/src/components/forms/context/ContextFormFieldSelect';
import { ContextFormFieldText } from '@bambi/component-library/src/components/forms/context/ContextFormFieldText';
import { ContextFormFieldTextArea } from '@bambi/component-library/src/components/forms/context/ContextFormFieldTextArea';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { PassengerAddressDataGrid } from '../PassengerAddresses/PassengerAddressDataGrid';
import AllowSMSFormContextField from './AllowSMSFormContextField';
import { ContextFormFieldSwitch } from 'src/common/FormField/v2/context/ContextFormFieldSwitch';
import { MultiSelectPayerField } from 'src/common/MultiSelectPayerContextField';
import { SectionHeading } from 'src/common/primitives/Section/SectionHeading';
import { pronounOptions } from 'src/common/pronounOptions';
import { sexOptions } from 'src/common/sexOptions';
import { useUserRoles } from 'src/features/auth/useUserRoles';
import { useIsRideOrderingPortalEnabledForOrganization } from 'src/features/rideOrderingPortal/useIsRideOrderingPortalEnabledForOrganization';
import { useShouldShowRideOrderingPortalFeatures } from 'src/features/rideOrderingPortal/useShouldShowRideOrderingPortalFeatures';

interface PassengerFormProps {
  passengerId?: string;
}
export function PassengerForm({ passengerId }: PassengerFormProps) {
  const isEditing = !!passengerId;
  const shouldShowRideOrderingPortalFeatures =
    useShouldShowRideOrderingPortalFeatures();
  const isRideOrderingPortalEnabledForOrganization =
    useIsRideOrderingPortalEnabledForOrganization();

  const { isAdmin, isAssociate, isDispatcher, isBambi, isOwner } =
    useUserRoles();

  const canEditAddressList =
    isAdmin || isAssociate || isDispatcher || isBambi || isOwner;

  return (
    <div data-testid="passenger-form" className="outline-none">
      <div className="mt-4">
        <div className="mt-4 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
          <div className="sm:col-span-2">
            <ContextFormFieldText
              fieldPath="first_name"
              label="First Name"
              placeholder="First Name"
              rules={{ required: 'This field is required' }}
            />
          </div>
          <div className="sm:col-span-2">
            <ContextFormFieldText
              fieldPath="last_name"
              label="Last Name"
              placeholder="Last Name"
              rules={{ required: 'This field is required' }}
            />
          </div>
          <div className="sm:col-span-2">
            <ContextFormFieldText
              fieldPath="preferred_name"
              label="Preferred Name"
              placeholder="Preferred Name"
            />
          </div>
          <div className="sm:col-span-2">
            <ContextFormFieldPhone
              fieldPath="phone_number"
              label="Phone number"
              rules={{
                validate: (value: string) =>
                  !value ||
                  isValidPhoneNumber(value) ||
                  'Please enter a valid phone number',
              }}
            />
          </div>
          <div className="sm:col-span-2">
            <ContextFormFieldEmail
              fieldPath="email"
              label="Email address"
              placeholder="Email Address"
            />
          </div>
          <div className="sm:col-span-2">
            <AllowSMSFormContextField />
          </div>
          <div className="sm:col-span-4">
            <ContextFormFieldDate fieldPath="dob" label="Date of Birth" />
          </div>
          <div className="sm:col-span-2">
            <ContextFormFieldSwitch
              fieldPath="is_student"
              label="Student/minor?"
            />
          </div>
          <div className="sm:col-span-2">
            <ContextFormFieldSelect
              fieldPath="gender"
              label="Sex"
              options={sexOptions}
              unwrapValue
            />
          </div>
          <div className="sm:col-span-2">
            <ContextFormFieldSelect
              fieldPath="pronoun"
              label="Preferred Pronoun"
              options={pronounOptions}
              unwrapValue
            />
          </div>
          <div className="sm:col-span-4">
            <ContextFormFieldNumber
              fieldPath="weight"
              label="Weight"
              placeholder="Enter weight"
            />
          </div>
          <div className="sm:col-span-2">
            <ContextFormFieldSwitch
              fieldPath="needs_bariatric_transport"
              label="Bariatric?"
            />
          </div>
          {!shouldShowRideOrderingPortalFeatures ? (
            <>
              <div className="mt-4 sm:col-span-6">
                <ContextFormFieldTextArea
                  fieldPath="notes"
                  label="Additional Notes (optional, visible to drivers)"
                />
              </div>
              <div className="mt-4 sm:col-span-6">
                <ContextFormFieldTextArea
                  fieldPath="dispatcher_notes"
                  label="Dispatcher Notes (optional, NOT visible to drivers)"
                />
              </div>
            </>
          ) : null}
          {
            // Currently, we only add passengers through the trip form, so this is only needed when editing
            // There's some backend work on the /passengers/ POST endpoint to accept payer_ids. Hiding this
            // lets us punt that til we want to add passenger creation to the passenger list page
            isEditing && isRideOrderingPortalEnabledForOrganization && (
              <div className="max-w-[30rem] sm:col-span-6">
                <MultiSelectPayerField fieldPath="payer_ids" />
              </div>
            )
          }
          {isEditing && canEditAddressList && (
            <div className="sm:col-span-6">
              <hr className="my-4 border-gray-200" />
              <SectionHeading heading="Passenger Address Information" />
              <PassengerAddressDataGrid passengerId={passengerId} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
